import * as THREE from '@phdsoft/three';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { registerCss2dLabel, unregisterCss2dLabel } from './css2d-label-registry';

/**
 * Each LabelData represents:
 *  - line: a flat array of numbers in groups of three [x₁, y₁, z₁, x₂, y₂, z₂, …, xₙ, yₙ, zₙ]
 *          (each consecutive triplet is one vertex of the polyline)
 *  - label: a numeric or string value (converted to text when rendering).
 */
export interface LabelData {
  line: number[];   // [x₁, y₁, z₁,  x₂, y₂, z₂, … , xₙ, yₙ, zₙ]
  label: number | string;
}

/**
 * After creating CSS2DObjects, we can keep references in case we need to update or remove them.
 */
export interface Label2DInfo {
  object2d: CSS2DObject;
  midpoint: THREE.Vector3;
}

/**
 * Each GeometryLabelData represents a label positioned at a specific 3D point (geometry center).
 */
export interface GeometryLabelData {
  position: THREE.Vector3;  // Direct 3D position for the label
  label: number | string;
}

/**
 * Call this once (after your scene & camera exist) to:
 *  1) Instantiate a CSS2DRenderer and insert its DOM element on top of your WebGL canvas.
 *  2) Given a list of LabelData, create a CSS2DObject (a <div>) at the midpoint of each polyline.
 *  3) Return an array of Label2DInfo so you can update positions if lines move or remove later.
 *
 * Usage:
 *
 *   // 1) After setting up your THREE.Scene, THREE.PerspectiveCamera, THREE.WebGLRenderer, and HTML container:
 *   const labelRenderer = initCSS2DRenderer(containerElement);
 *
 *   // 2) Create your LabelData[] (each with a "line" flat array and a "label").
 *   const labels: LabelData[] = [
 *     { line: [0,0,0,  1,1,0,  2,1,1], label: 'A' },
 *     { line: [-1,1,2,  1,2,3], label: 'B' },
 *     // …
 *   ];
 *
 *   // 3) Call createLabel2DObjects(scene, labels):
 *   const labelInfos = createLabel2DObjects(scene, labels);
 *
 *   // 4) In your render loop (after renderer.render(scene, camera)):
 *     renderer.render(scene, camera);
 *     renderRegisteredCss2dLabels(labelRenderer, scene, camera);
 *     // was: labelRenderer.render(scene, camera);
 *
 *   // Only labels passed to registerCss2dLabel (css2d-label-registry.ts) are drawn;
 *   // createLabel2DObjects and createSimpleGeometryCenterLabels2DObjects register theirs.
 *
 *   // No manual scaling is needed—CSS2DObjects are always "screen‐pixel" size.
 *
 *   // 5) If you need to update or remove labels at runtime, use labelInfos[] references.
 */

/**
 * Initializes and returns a CSS2DRenderer configured to overlay on top of your WebGL canvas.
 *
 * @param containerEl The HTML element that holds your WebGL canvas (e.g. a <div id="canvasContainer">).
 * @returns CSS2DRenderer instance; call renderRegisteredCss2dLabels(labelRenderer, scene, camera) each frame.
 * (was: call labelRenderer.render(scene, camera) each frame.)
 */
export function initCSS2DRenderer(
  containerEl: HTMLElement
): CSS2DRenderer {
  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(containerEl.clientWidth, containerEl.clientHeight);
  labelRenderer.domElement.dataset.viewportLayer = 'css2d';
  labelRenderer.domElement.style.position = 'absolute';
  labelRenderer.domElement.style.top = '0';
  labelRenderer.domElement.style.left = '0';
  labelRenderer.domElement.style.pointerEvents = 'none';
  containerEl.appendChild(labelRenderer.domElement);

  // Resize handling: keep CSS2DRenderer in sync with WebGL canvas size.
  window.addEventListener('resize', () => {
    labelRenderer.setSize(containerEl.clientWidth, containerEl.clientHeight);
  });

  return labelRenderer;
}

/**
 * Creates an offscreen <canvas> containing the given text and measures its dimensions.
 */
function createTextCanvas(text: string): { canvas: HTMLCanvasElement; width: number; height: number } {
  const fontSize = 12; // Must match CSS font-size below
  const font = `bold ${fontSize}px Arial`;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = font;

  const metrics = ctx.measureText(text);
  // Add padding: 4px horizontally, 2px vertically
  const textWidth = Math.ceil(metrics.width) + 8;
  const textHeight = fontSize + 4;

  canvas.width = textWidth;
  canvas.height = textHeight;

  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'white';
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 2;
  ctx.translate(textWidth / 2, textHeight / 2);
  ctx.strokeText(text, 0, 0);
  ctx.fillText(text, 0, 0);

  return { canvas, width: textWidth, height: textHeight };
}

/**
 * Create polylines and CSS2DObject labels at whichever end of each line is farthest from centerPoint.
 * If that label overlaps a previously placed label in screen space, move it incrementally along the line
 * toward the centerPoint until no overlap or max attempts reached.
 * All lines and labels are added to `group`.
 *
 * @param group        The THREE.Group to add lines and labels.
 * @param labels       Array of LabelData.
 * @param centerPoint  A THREE.Vector3 representing the center of your geometries.
 * @param camera       The active THREE.Camera (used for projecting to screen).
 * @param renderer     The THREE.WebGLRenderer (for screen dimensions).
 * @returns            Array of Label2DInfo with final label positions.
 */
export function createLabel2DObjects(
  group: THREE.Group,
  labels: LabelData[],
  centerPoint: THREE.Vector3,
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer
): Label2DInfo[] {
  const infos: Label2DInfo[] = [];

  // Maximum number of offset steps to avoid infinite loops
  const MAX_OFFSET_STEPS = 5;
  // World‐unit step size along the line
  const OFFSET_STEP = 0.1;

  // Keep track of placed label boxes in screen‐space to avoid overlap
  const placedBoxes: Array<{ xMin: number; yMin: number; xMax: number; yMax: number }> = [];

  // Helper: project worldPos → screen coords (pixels)
  function projectToScreen(worldPos: THREE.Vector3): { x: number; y: number } {
    const ndc = worldPos.clone().project(camera);
    const width = renderer.domElement.clientWidth;
    const height = renderer.domElement.clientHeight;
    const x = (ndc.x * 0.5 + 0.5) * width;
    const y = (-ndc.y * 0.5 + 0.5) * height;
    return { x, y };
  }

  for (const lbl of labels) {
    const arr = lbl.line;
    const nPoints = Math.floor(arr.length / 3);
    if (nPoints < 2) continue; // need at least two points

    // 1) Build an array of Vector3 points from the flat number[]:
    const points: THREE.Vector3[] = [];
    for (let i = 0; i < nPoints; i++) {
      points.push(new THREE.Vector3(arr[i * 3], arr[i * 3 + 1], arr[i * 3 + 2]));
    }

    // 2) Create a polyline (connects points in order) and add to group
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0x0000ff });
    const polyline = new THREE.Line(geometry, material);
    group.add(polyline);

    // 3) Identify the start and end points of this polyline
    const firstPt = points[0];
    const lastPt = points[points.length - 1];

    // 4) Determine which end is farther from centerPoint
    const distStart = centerPoint.distanceTo(firstPt);
    const distEnd = centerPoint.distanceTo(lastPt);
    // farPt = label start; nearPt = direction to move if overlap
    const farPt = distStart < distEnd ? lastPt.clone() : firstPt.clone();
    const nearPt = distStart < distEnd ? firstPt.clone() : lastPt.clone();

    // 5) Compute unit direction from farPt → nearPt
    const lineDir = nearPt.clone().sub(farPt).normalize();

    // 6) Determine label position; initially at farPt
    let labelPos = farPt.clone();

    // 7) Create offscreen canvas for label text and measure
    const text = String(lbl.label);
    const { canvas, width: textWidth, height: textHeight } = createTextCanvas(text);

    // 8) Project initial labelPos → screen, build bounding box
    let screen = projectToScreen(labelPos);
    let box = {
      xMin: screen.x - textWidth / 2,
      xMax: screen.x + textWidth / 2,
      yMin: screen.y - textHeight / 2,
      yMax: screen.y + textHeight / 2
    };

    // 9) Check overlap with previously placed labels; if overlapping, shift along lineDir
    let offsetSteps = 0;
    while (
      placedBoxes.some(prev =>
        !(
          box.xMax < prev.xMin ||
          box.xMin > prev.xMax ||
          box.yMax < prev.yMin ||
          box.yMin > prev.yMax
        )
      ) &&
      offsetSteps < MAX_OFFSET_STEPS
    ) {
      labelPos.add(lineDir.clone().multiplyScalar(OFFSET_STEP));
      screen = projectToScreen(labelPos);
      box = {
        xMin: screen.x - textWidth / 2,
        xMax: screen.x + textWidth / 2,
        yMin: screen.y - textHeight / 2,
        yMax: screen.y + textHeight / 2
      };
      offsetSteps++;
    }

    // 10) Create <div> for this label and style it
    const div = document.createElement('div');
    div.className = 'label2d';
    div.textContent = text;
    div.style.padding = '2px 6px';
    div.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
    div.style.color = '#fff';
    div.style.fontSize = '12px';
    div.style.whiteSpace = 'nowrap';
    div.style.borderRadius = '4px';
    div.style.pointerEvents = 'none';
    div.style.textShadow = '0px 0px 3px black';

    // 11) Wrap in CSS2DObject and position at labelPos
    const labelObj = new CSS2DObject(div);
    labelObj.position.copy(labelPos);
    if(text != "Unlabeled" && text != ""){
      group.add(labelObj);
      registerCss2dLabel(labelObj);
    }

    // 12) Store bounding box and Label2DInfo
    placedBoxes.push(box);
    infos.push({ object2d: labelObj, midpoint: labelPos.clone() });
  }

  return infos;
}

/**
 * Updates the scale and opacity of existing labels based on current camera distance.
 * Call this function when the camera moves to update label scales and opacity dynamically.
 * 
 * @param labelInfos   Array of Label2DInfo returned by createSimpleGeometryCenterLabels2DObjects().
 * @param camera       The active THREE.Camera for distance calculation.
 * @param minScale     Minimum scale factor (default: 0.5).
 * @param maxScale     Maximum scale factor (default: 3).
 * @param maxDistance  Maximum distance for scaling (default: 25).
 * @param minOpacity   Minimum opacity for distant objects (default: 0.3).
 * @param maxOpacity   Maximum opacity for close objects (default: 1.0).
 */
export function updateLabel2DScales(
  labelInfos: Label2DInfo[],
  camera: THREE.Camera,
  minScale: number = .5,
  maxScale: number = 2,
  maxDistance: number = 25,
  minOpacity: number = 0.3,
  maxOpacity: number = 1.0
): void {
  for (const info of labelInfos) {
    const distance = camera.position.distanceTo(info.midpoint);
    const normalizedDistance = Math.min(distance / maxDistance, 1);
    // Invert the scale: closer objects get larger scale, farther objects get smaller scale
    const scale = minScale + ((1 - normalizedDistance) * (maxScale - minScale));
    
    // Calculate opacity: closer objects get higher opacity, farther objects get lower opacity
    const opacity = minOpacity + ((1 - normalizedDistance) * (maxOpacity - minOpacity));
    
    const baseFontSize = 12;
    const scaledFontSize = Math.round(baseFontSize * scale);
    
    const div = info.object2d.element as HTMLDivElement;
    div.style.fontSize = `${scaledFontSize}px`;
    
    // Update opacity for background and border
    div.style.backgroundColor = `rgba(0, 0, 0, ${0.8 * opacity})`;
    div.style.border = `1px solid rgba(255, 255, 255, ${0.3 * opacity})`;
  }
}

/**
 * If your lines/label midpoints move (e.g. dynamic geometry), call this each frame or on update:
 * For each Label2DInfo, update object2d.position to the current midpoint of its polyline.
 *
 * @param labelInfos   Array of Label2DInfo returned by createLabel2DObjects().
 * @param labels       The current array of LabelData (so you can recompute midpoints).
 */
export function updateLabel2DPositions(
  labelInfos: Label2DInfo[],
  labels: LabelData[]
): void {
  for (let i = 0; i < labelInfos.length; i++) {
    const info = labelInfos[i];
    const lbl = labels[i];
    const arr = lbl.line;
    const nPoints = Math.floor(arr.length / 3);
    if (nPoints < 2) continue;

    const firstPt = new THREE.Vector3(arr[0], arr[1], arr[2]);
    const iLast = (nPoints - 1) * 3;
    const lastPt = new THREE.Vector3(arr[iLast], arr[iLast + 1], arr[iLast + 2]);
    const midpoint = info.midpoint.copy(firstPt).add(lastPt).multiplyScalar(0.5);

    info.object2d.position.copy(midpoint);
  }
}

export function disposeGroupLinesAndLabels(group: THREE.Group): void {
  const linesToDispose: THREE.Line[] = [];
  const labelsToDispose: CSS2DObject[] = [];

  // Collect all THREE.Line and CSS2DObject instances in the group (including nested children)
  group.traverse((obj) => {
    if (obj instanceof THREE.Line) {
      linesToDispose.push(obj);
    } else if (obj instanceof CSS2DObject) {
      labelsToDispose.push(obj);
    }
  });

  // Dispose and remove each line
  for (const line of linesToDispose) {
    // Dispose geometry
    if (line.geometry) {
      line.geometry.dispose();
    }
    // Dispose material
    if (Array.isArray(line.material)) {
      for (const mat of line.material) {
        mat.dispose();
      }
    } else {
      line.material.dispose();
    }
    // Remove from parent
    if (line.parent) {
      line.parent.remove(line);
    }
  }

  // Dispose and remove each CSS2DObject
  for (const labelObj of labelsToDispose) {
    unregisterCss2dLabel(labelObj);
    // Remove the HTML element from the DOM
    const el = labelObj.element;
    if (el.parentElement) {
      el.parentElement.removeChild(el);
    }
    // Remove from parent in the scene graph
    if (labelObj.parent) {
      labelObj.parent.remove(labelObj);
    }
  }
}

/**
 * Creates CSS2DObject labels at specific 3D positions (geometry centers) with optional distance-based scaling and opacity.
 * Labels scale and fade based on distance from camera - farther objects have smaller and more transparent labels.
 * 
 * @param group        The THREE.Group to add labels (no lines will be added).
 * @param geometryLabels Array of GeometryLabelData with direct positions.
 * @param camera       The active THREE.Camera for distance calculation (optional).
 * @param minScale     Minimum scale factor (default: 0.1).
 * @param maxScale     Maximum scale factor (default: 3).
 * @param maxDistance  Maximum distance for scaling (default: 100).
 * @param minOpacity   Minimum opacity for distant objects (default: 0.3).
 * @param maxOpacity   Maximum opacity for close objects (default: 1.0).
 * @returns            Array of Label2DInfo with label positions at geometry centers.
 */
export function createSimpleGeometryCenterLabels2DObjects(
  group: THREE.Group,
  geometryLabels: GeometryLabelData[],
  camera?: THREE.Camera,
  minScale: number = 0.1,
  maxScale: number = 3,
  maxDistance: number = 100,
  minOpacity: number = 0.3,
  maxOpacity: number = 1.0
): Label2DInfo[] {
  const infos: Label2DInfo[] = [];

  for (const geometryLabel of geometryLabels) {

    const labelPos = geometryLabel.position.clone();

    // Calculate scale and opacity based on distance if camera is provided
    let scaledFontSize = 12; // Default font size
    let opacity = 1; // Default opacity
    if (camera) {
      const distance = camera.position.distanceTo(labelPos);
      const normalizedDistance = Math.min(distance / maxDistance, 1);
      // Invert the scale: closer objects get larger scale, farther objects get smaller scale
      const scale = minScale + ((1 - normalizedDistance) * (maxScale - minScale));
      scaledFontSize = Math.round(12 * scale);
      
      // Calculate opacity: closer objects get higher opacity, farther objects get lower opacity
      opacity = minOpacity + ((1 - normalizedDistance) * (maxOpacity - minOpacity));
      

    }

    const text = String(geometryLabel.label);
    const div = document.createElement('div');
    div.className = 'label2d';
    
    // Use innerHTML for multi-line support
    if (text.includes('\n')) {
      // Format as a multi-line list
      div.innerHTML = text.replace(/\n/g, '<br/>');
    } else {
      div.textContent = text;
    }
    
    div.style.padding = '4px 8px';
    div.style.backgroundColor = `rgba(0, 0, 0, ${0.8 * opacity})`;
    div.style.color = '#fff';
    div.style.fontSize = `${scaledFontSize}px`;
    div.style.fontWeight = 'bold';
    div.style.whiteSpace = 'pre-wrap';
    div.style.borderRadius = '4px';
    div.style.pointerEvents = 'none';
    div.style.textShadow = '0px 0px 3px black';
    div.style.border = `1px solid rgba(255, 255, 255, ${0.3 * opacity})`;
    div.style.lineHeight = '1.2';
    div.style.maxWidth = '200px';

    const labelObj = new CSS2DObject(div);
    labelObj.position.copy(labelPos);
    
    if(text != "Unlabeled" && text != ""){
      group.add(labelObj);
      registerCss2dLabel(labelObj);
    }

    infos.push({ object2d: labelObj, midpoint: labelPos.clone() });
  }

  return infos;
}
